import { LoaderCircle, Plus, Save, Trash2, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { useEmpresa } from '../../context/EmpresaContext'
import { supabase } from '../../lib/supabase'
import { normalizarMoneda } from '../../utils/numeros'
import {
  OPCIONES_MEDIO_PAGO_POS,
  formatearMontoPos,
} from '../../utils/pos'

// Medios que además de monto admiten una referencia (N.º de
// transferencia o de cheque). Igual criterio que en Nueva venta.
const MEDIOS_CON_REFERENCIA = ['transferencia', 'cheque']

// Monto de pago: dinero, no decimal físico — un punto es separador de
// miles ("30.000" -> 30000). Ver normalizarMoneda.
function convertirMonto(valor) {
  const numero = normalizarMoneda(valor)

  return Number.isFinite(numero) ? numero : 0
}

function crearPagoVacio(idLocal) {
  return {
    idLocal,
    medio_pago: '',
    monto: '',
    referencia: '',
  }
}

function RegistrarPagoPosModal({ abierto, venta, onCerrar, onExito }) {
  const { empresaActual } = useEmpresa()

  const [pagos, setPagos] = useState([])
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')

  const contadorPagoRef = useRef(0)

  useEffect(() => {
    if (abierto) {
      contadorPagoRef.current = 0
      setPagos([])
      setError('')
    }
  }, [abierto])

  useEffect(() => {
    if (!abierto) return

    const alPresionarTecla = (event) => {
      if (event.key === 'Escape' && !guardando) {
        onCerrar?.()
      }
    }

    document.addEventListener('keydown', alPresionarTecla)

    return () => {
      document.removeEventListener('keydown', alPresionarTecla)
    }
  }, [abierto, guardando, onCerrar])

  if (!abierto || !venta) return null

  const saldoPendiente = Number(venta.saldo_pendiente) || 0

  const pagosConMonto = pagos.map((pago) => ({
    ...pago,
    montoNumero: convertirMonto(pago.monto),
  }))

  const totalARegistrar = pagosConMonto.reduce(
    (acumulado, pago) => acumulado + pago.montoNumero,
    0
  )

  const nuevoSaldo = saldoPendiente - totalARegistrar

  const siguienteIdPago = () => {
    contadorPagoRef.current += 1
    return contadorPagoRef.current
  }

  const agregarPago = () => {
    setPagos((actuales) => [
      ...actuales,
      crearPagoVacio(siguienteIdPago()),
    ])
  }

  const eliminarPago = (idLocal) => {
    setPagos((actuales) =>
      actuales.filter((pago) => pago.idLocal !== idLocal)
    )
  }

  const cambiarCampoPago = (idLocal, campo, valor) => {
    setPagos((actuales) =>
      actuales.map((pago) =>
        pago.idLocal === idLocal ? { ...pago, [campo]: valor } : pago
      )
    )
  }

  const alHacerClicFuera = (event) => {
    if (guardando) return

    if (event.target === event.currentTarget) {
      onCerrar?.()
    }
  }

  const manejarEnvio = async (event) => {
    event.preventDefault()

    if (guardando) return

    setError('')

    if (pagosConMonto.length === 0) {
      setError('Debes agregar al menos un pago.')
      return
    }

    for (const pago of pagosConMonto) {
      if (!pago.medio_pago) {
        setError('Debes seleccionar el medio de pago en cada fila.')
        return
      }

      if (pago.montoNumero <= 0) {
        setError('El monto de cada pago debe ser mayor que cero.')
        return
      }
    }

    if (totalARegistrar > saldoPendiente) {
      setError(
        'El total de los pagos no puede superar el saldo pendiente de la venta.'
      )
      return
    }

    try {
      setGuardando(true)

      const { error: errorRpc } = await supabase.rpc(
        'registrar_pagos_venta_pos',
        {
          p_empresa_id: empresaActual.id,
          p_venta_id: venta.id,
          p_pagos: pagosConMonto.map((pago) => ({
            fecha_pago: new Date().toISOString(),
            medio_pago: pago.medio_pago,
            monto: pago.montoNumero,
            referencia: pago.referencia.trim() || null,
            observaciones: null,
          })),
        }
      )

      if (errorRpc) throw errorRpc

      onExito?.()
    } catch (errorRegistro) {
      console.error(
        'Error al registrar pagos de venta POS:',
        errorRegistro
      )

      setError(
        errorRegistro.message ||
          'No fue posible registrar los pagos.'
      )
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={alHacerClicFuera}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="registrar-pago-pos-titulo"
        className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl bg-white shadow-xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-6">
          <h2
            id="registrar-pago-pos-titulo"
            className="text-lg font-bold text-slate-900"
          >
            Registrar pago
          </h2>

          <button
            type="button"
            onClick={onCerrar}
            disabled={guardando}
            aria-label="Cerrar"
            className="shrink-0 text-slate-400 transition hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <X size={22} />
          </button>
        </div>

        <form
          onSubmit={manejarEnvio}
          className="flex-1 overflow-y-auto p-6"
        >
          <div className="mb-6 rounded-xl bg-slate-50 p-4 text-center">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Saldo pendiente
            </p>

            <p className="mt-1 text-2xl font-bold text-slate-900">
              {formatearMontoPos(saldoPendiente)}
            </p>
          </div>

          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Pagos
            </h3>

            <button
              type="button"
              onClick={agregarPago}
              disabled={guardando}
              className="flex items-center justify-center gap-2 rounded-lg border border-emerald-600 px-4 py-2.5 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Plus size={18} />
              Agregar forma de pago
            </button>
          </div>

          {pagos.length === 0 && (
            <p className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
              Aún no has agregado pagos.
            </p>
          )}

          <div className="space-y-4">
            {pagos.map((pago, indice) => {
              const mostrarReferencia = MEDIOS_CON_REFERENCIA.includes(
                pago.medio_pago
              )

              return (
                <div
                  key={pago.idLocal}
                  className="rounded-xl border border-slate-200 p-4"
                >
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                      Pago {indice + 1}
                    </p>

                    <button
                      type="button"
                      onClick={() => eliminarPago(pago.idLocal)}
                      disabled={guardando}
                      title="Eliminar pago"
                      aria-label="Eliminar pago"
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-red-200 text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Campo label="Medio de pago" requerido>
                      <select
                        value={pago.medio_pago}
                        onChange={(event) =>
                          cambiarCampoPago(
                            pago.idLocal,
                            'medio_pago',
                            event.target.value
                          )
                        }
                        disabled={guardando}
                        className={estiloInput}
                      >
                        <option value="">Seleccionar medio</option>

                        {OPCIONES_MEDIO_PAGO_POS.map((opcion) => (
                          <option
                            key={opcion.valor}
                            value={opcion.valor}
                          >
                            {opcion.etiqueta}
                          </option>
                        ))}
                      </select>
                    </Campo>

                    <Campo label="Monto" requerido>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={pago.monto}
                        onChange={(event) =>
                          cambiarCampoPago(
                            pago.idLocal,
                            'monto',
                            event.target.value
                          )
                        }
                        placeholder="0"
                        disabled={guardando}
                        className={estiloInput}
                      />
                    </Campo>

                    {mostrarReferencia && (
                      <div className="sm:col-span-2">
                        <Campo
                          label={
                            pago.medio_pago === 'cheque'
                              ? 'N° cheque / referencia'
                              : 'Referencia (opcional)'
                          }
                        >
                          <input
                            type="text"
                            value={pago.referencia}
                            onChange={(event) =>
                              cambiarCampoPago(
                                pago.idLocal,
                                'referencia',
                                event.target.value
                              )
                            }
                            placeholder={
                              pago.medio_pago === 'cheque'
                                ? 'Ej: CH-001245'
                                : 'Ej: TRX-123456'
                            }
                            disabled={guardando}
                            className={estiloInput}
                          />
                        </Campo>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <ResumenPago
                etiqueta="Saldo actual"
                valor={formatearMontoPos(saldoPendiente)}
              />

              <ResumenPago
                etiqueta="Total a registrar"
                valor={formatearMontoPos(totalARegistrar)}
              />

              <ResumenPago
                etiqueta="Nuevo saldo"
                valor={formatearMontoPos(Math.max(nuevoSaldo, 0))}
              />
            </div>
          </div>

          {error && (
            <div
              role="alert"
              className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onCerrar}
              disabled={guardando}
              className="w-full rounded-lg border border-slate-300 px-5 py-2.5 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={guardando}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400 sm:w-auto"
            >
              {guardando ? (
                <LoaderCircle
                  size={18}
                  className="animate-spin"
                />
              ) : (
                <Save size={18} />
              )}
              {guardando ? 'Registrando...' : 'Registrar pago'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const estiloInput =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-3 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100'

function Campo({ label, requerido = false, children }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-slate-700">
        {label}

        {requerido && (
          <span className="ml-1 text-red-500">*</span>
        )}
      </label>

      {children}
    </div>
  )
}

function ResumenPago({ etiqueta, valor }) {
  return (
    <div className="rounded-lg bg-white p-3 text-center shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {etiqueta}
      </p>

      <p className="mt-1 text-sm font-bold text-slate-900">
        {valor}
      </p>
    </div>
  )
}

export default RegistrarPagoPosModal
